import { Select } from '@ardoq/select';
import { NumberInput, Label, useOptimistic } from '@ardoq/forms';
import { zIndex } from '@ardoq/design-tokens';
import { getDropdownMenuPortalTarget } from '@ardoq/dropdown-menu';
import { Text } from '@ardoq/typography';
import { Stack, Inline, FlexBox, Box } from '@ardoq/layout';
import { t, type WithI18nLocale } from '@ardoq/i18n';
import type { LabelFilterContentViewProps } from './types';

type LabelFilterContentViewComponentProps = LabelFilterContentViewProps &
  WithI18nLocale;

export const LabelFilterContentView = ({
  description,
  fieldSelect: { label, placeholder, value, options },
  rangeInputs,
  actions: { onFieldChange, onValueChange, onValueInputBlur },
  i18nLocale,
}: LabelFilterContentViewComponentProps) => {
  const [minValue, onMinValueChange] = useOptimistic(
    rangeInputs?.min.value,
    (value: number) => onValueChange(value, 'min')
  );
  const [maxValue, onMaxValueChange] = useOptimistic(
    rangeInputs?.max.value,
    (value: number) => onValueChange(value, 'max')
  );

  return (
    <Box style={{ width: '320px' }} padding="small" paddingTop="xsmall">
      <Stack gap="medium">
        <Text color="textModerate" variant="caption">
          {description}
        </Text>
        <Select
          label={label}
          placeholder={placeholder}
          menuPlacement="bottom"
          menuPortalTarget={getDropdownMenuPortalTarget()}
          isClearable
          value={value}
          options={options}
          onValueChange={onFieldChange}
          styles={{
            menuPortal: base => ({
              ...base,
              zIndex: zIndex.MODAL_BACKDROP + 1,
            }),
          }}
          i18nLocale={i18nLocale}
        />

        {rangeInputs ? (
          <Stack gap="small">
            <Label>{rangeInputs.label}</Label>
            <Inline gap="small">
              <FlexBox flex={1}>
                <NumberInput
                  aria-label={t(
                    i18nLocale,
                    'tabview:bubbleChart.labelFilter.minAriaLabel',
                    'Min'
                  )}
                  allowDecimals
                  value={minValue}
                  onValueChange={onMinValueChange}
                  onBlurTransformValue={value => onValueInputBlur(value, 'min')}
                  upArrowDisabled={
                    minValue !== undefined &&
                    maxValue !== undefined &&
                    minValue >= maxValue
                  }
                  placeholder={rangeInputs.min.placeholder}
                />
              </FlexBox>
              <FlexBox flex={1}>
                <NumberInput
                  aria-label={t(
                    i18nLocale,
                    'tabview:bubbleChart.labelFilter.maxAriaLabel',
                    'Max'
                  )}
                  allowDecimals
                  value={maxValue}
                  onValueChange={onMaxValueChange}
                  onBlurTransformValue={value => onValueInputBlur(value, 'max')}
                  downArrowDisabled={
                    minValue !== undefined &&
                    maxValue !== undefined &&
                    maxValue <= minValue
                  }
                  placeholder={rangeInputs.max.placeholder}
                />
              </FlexBox>
            </Inline>
          </Stack>
        ) : null}
      </Stack>
    </Box>
  );
};
